import { useState } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, Outlet } from 'react-router-dom';
import { Header } from './components/Header';
import { Sidebar } from './components/Sidebar';
import { Dashboard } from './pages/Dashboard';
import { PricingPlans } from './pages/catalog/Plans';
import { FeaturesPage } from './pages/catalog/Features';
import { Organisations } from './pages/Organisations';
import { PricingCalculator } from './pages/catalog/Calculator';
import LogsPage from './pages/ops/Logs';
import { ServiceStatus } from './pages/ops/Status';
import SyncCleanup from './pages/ops/SyncCleanup';
import InvoiceListWrapper from './pages/InvoiceListWrapper';
import InvoiceDetailWrapper from './pages/InvoiceDetailWrapper';
import InvoiceCreateWrapper from './pages/InvoiceCreateWrapper';
import { Settings } from './pages/Settings';
import { ApplicationQueue } from './pages/signup/ApplicationList';
import { ApplicationDetail } from './pages/signup/ApplicationDetail';
import { BlocklistManagement } from './pages/signup/Blocklist';
import { PartnerManagement } from './pages/access/Partners';
import { ThemeProvider } from './contexts/ThemeProvider';
import { AuthProvider, ProtectedRoute } from './contexts/AuthProvider';
import Login from './pages/Login';
import MfaSetup from './pages/MfaSetup';
import MfaVerify from './pages/MfaVerify';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
import AcceptInvitation from './pages/AcceptInvitation';
import { AdminUsers } from './pages/access/PortalUsers';
import { ApiInspector } from './components/ApiInspector';
import { OrgDetail } from './pages/orgs/OrgDetail';
import OrgOverview from './pages/orgs/tabs/Overview';
import OrgProjects from './pages/orgs/tabs/Projects';
import OrgUsers from './pages/orgs/tabs/Users';
import OrgStreaming from './pages/orgs/tabs/Streaming';
import OrgBilling from './pages/orgs/tabs/Billing';
import OrgActivity from './pages/orgs/tabs/Activity';
import { ROUTE_REDIRECTS } from './routes/redirects';
import './App.css';
import './theme.css';

/** Layout shell: Header (60px) + Sidebar (250px collapsible) + Content */
function AppLayout() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  return (
    <div className="min-h-screen bg-base-100 text-base-content">
      <Header
        mobileMenuOpen={mobileMenuOpen}
        onToggleMobileMenu={() => setMobileMenuOpen(!mobileMenuOpen)}
      />
      <div className="flex pt-[60px]">
        <Sidebar mobileOpen={mobileMenuOpen} onMobileClose={() => setMobileMenuOpen(false)} />
        <main className="flex-1 min-w-0 p-4 lg:p-6">
          <Outlet />
        </main>
      </div>
    </div>
  );
}

function App() {
  return (
    <ThemeProvider>
      <Router>
        <AuthProvider>
          <Routes>
            {/* Public auth routes — no layout shell */}
            <Route path="/login" element={<Login />} />
            <Route path="/mfa-setup" element={<MfaSetup />} />
            <Route path="/mfa-verify" element={<MfaVerify />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/accept-invitation" element={<AcceptInvitation />} />

            {/* Protected routes — Header + Sidebar + auth guard */}
            <Route element={<ProtectedRoute />}>
              <Route element={<AppLayout />}>
                <Route path="/" element={<Navigate to="/dashboard" replace />} />
                <Route path="/dashboard" element={<Dashboard />} />

                {/* Orgs — list + detail with 5 tabs; the standalone Projects
                    page was retired in 53c.3 (content lives in OrgDetail's
                    Projects tab). The legacy /projects URL still redirects
                    to /orgs via ROUTE_REDIRECTS. */}
                <Route path="/orgs" element={<Organisations />} />
                <Route path="/orgs/:id" element={<OrgDetail />}>
                  <Route index element={<OrgOverview />} />
                  <Route path="projects" element={<OrgProjects />} />
                  <Route path="users" element={<OrgUsers />} />
                  <Route path="streaming" element={<OrgStreaming />} />
                  <Route path="billing" element={<OrgBilling />} />
                  <Route path="activity" element={<OrgActivity />} />
                </Route>

                {/* Signup Queue */}
                <Route path="/signup-queue" element={<ApplicationQueue />} />
                <Route path="/signup-queue/:id" element={<ApplicationDetail />} />
                <Route path="/signup-queue/blocklist" element={<BlocklistManagement />} />

                {/* Catalog */}
                <Route path="/catalog/plans" element={<PricingPlans />} />
                <Route path="/catalog/features" element={<FeaturesPage />} />
                <Route path="/catalog/calculator" element={<PricingCalculator />} />

                {/* Access */}
                <Route path="/access/users" element={<AdminUsers />} />
                <Route path="/access/partners" element={<PartnerManagement />} />

                {/* Operations */}
                <Route path="/ops/sync" element={<SyncCleanup />} />
                <Route path="/ops/logs" element={<LogsPage />} />
                <Route path="/ops/status" element={<ServiceStatus />} />

                <Route path="/settings" element={<Settings />} />

                {/* Invoices stay under their existing nesting; linked from Org Detail in 53c.2 */}
                <Route path="/invoices/:organisationId" element={<InvoiceListWrapper />} />
                <Route path="/invoices/:organisationId/create" element={<InvoiceCreateWrapper />} />
                <Route
                  path="/invoices/:organisationId/:invoiceId"
                  element={<InvoiceDetailWrapper />}
                />

                {/* Old → new redirects so external bookmarks and Slack permalinks don't 404 */}
                {Object.entries(ROUTE_REDIRECTS).map(([oldPath, newPath]) => (
                  <Route key={oldPath} path={oldPath} element={<Navigate to={newPath} replace />} />
                ))}
                <Route path="/applications/:id" element={<RedirectApplicationDetail />} />
              </Route>
            </Route>
          </Routes>
        </AuthProvider>
      </Router>
      {process.env.NODE_ENV === 'development' && <ApiInspector />}
    </ThemeProvider>
  );
}

/** Preserve `/applications/:id` deep links — redirect to `/signup-queue/:id`. */
function RedirectApplicationDetail() {
  const id = window.location.pathname.split('/').pop();
  return <Navigate to={`/signup-queue/${id ?? ''}`} replace />;
}

export default App;
